'use client'

import { useState } from 'react'
import type { IChannelAccess, IChannelAccessEntry, ILoggedInUser } from 'types'
import Avatar from 'ui/src/components/avatar'
import ChevronIcon from './chevron-icon'

export const rowClassName =
    'flex w-full cursor-pointer items-center gap-2 truncate px-4 py-1.5 text-left text-sm text-white/75 hover:text-white'

export const dividerClassName = 'my-1.5 border-t border-giphyLightCharcoal/30'

// Legacy's `CheckMarkIcon` (navbar-dropdown/style.tsx) — the currently
// active channel/account gets this, unmodified in shape.
const CheckMarkIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" className="ml-auto shrink-0" aria-hidden="true">
        <defs>
            <linearGradient id="user-menu-checkmark" x1="100%" x2="0%" y1="0%" y2="100%">
                <stop offset="0%" stopColor="#0F9" />
                <stop offset="100%" stopColor="#0CF" />
            </linearGradient>
        </defs>
        <path
            fill="url(#user-menu-checkmark)"
            fillRule="evenodd"
            d="M12 0c6.627 0 12 5.373 12 12s-5.373 12-12 12S0 18.627 0 12 5.373 0 12 0zm5.245 6a.257.257 0 00-.201.089l-5.98 6.732-2.608-2.944a.257.257 0 00-.2-.09.257.257 0 00-.201.09l-1.97 2.212a.312.312 0 00-.085.225c0 .092.029.167.086.226l4.774 5.371c.075.06.143.089.204.089.062 0 .13-.03.204-.089l8.146-9.159a.312.312 0 00.086-.225.312.312 0 00-.086-.226l-1.969-2.212a.257.257 0 00-.2-.089z"
        />
    </svg>
)

// Legacy's `AccessUserIcon` — shown on the "real" account row while
// currently emulating a different channel, offering the way back.
const AccessUserIcon = () => (
    <svg width="14" height="14" viewBox="0 0 15 15" fill="none" className="ml-auto shrink-0" aria-hidden="true">
        <path
            d="M14.5 13.6146C14.5 10.6609 9.18748 11.2558 9.18748 8.89005C9.18748 8.89005 10.9583 7.11922 10.9583 3.87499C10.9583 1.92431 9.36733 0.333328 7.41665 0.333328C5.46596 0.333328 3.87498 1.92431 3.87498 3.87499C3.87498 7.11922 5.64581 8.89005 5.64581 8.89005C5.64581 11.2558 0.333313 10.6609 0.333313 13.6146V14.2026C0.333313 14.3686 0.464742 14.5 0.630758 14.5H14.2025C14.3686 14.5 14.5 14.3686 14.5 14.2026V13.6146Z"
            fill="#D8D8D8"
        />
    </svg>
)

type AccessEntryProps = { access: IChannelAccessEntry; isActive: boolean }

const AccessEntry = ({ access, isActive }: AccessEntryProps) => (
    <li>
        <a href={access.login_url} className={rowClassName}>
            <Avatar
                user={{ avatar_url: access.channel.avatar_url, display_name: access.channel.display_name }}
                width={20}
                height={20}
                className="shrink-0 rounded-sm"
            />
            <span className="flex-1 truncate">{access.channel.display_name}</span>
            {isActive && <CheckMarkIcon />}
        </a>
    </li>
)

type Props = { user: ILoggedInUser; channelAccess: IChannelAccess }

/**
 * Ported from legacy's `network-channels.tsx` (`NetworkChannels`) — item #6
 * of the dropdown's item list (index.tsx's own comment), gated by the
 * parent on `!!channel_access`. Reproduces both of that file's real
 * conditionals:
 *  - the "switch back to me" row only renders when `channel_access.user` is
 *    present, and swaps its own check/person icon on `emulated_user`
 *  - past 10 entries, only the first renders by default with a "See All
 *    Accounts" toggle (`access.length`, not a hardcoded count) rather than
 *    all of them at once
 * Not reproduced: legacy's fixed 245px scroll height + bottom fade-out
 * gradient on the expanded list — decision #1 (own visual language, not
 * legacy's pixel styling) applies here specifically to that chrome, not to
 * either conditional above.
 */
const ChannelAccessList = ({ user, channelAccess }: Props) => {
    const [showAll, setShowAll] = useState(false)
    const { access, emulated_user, user: accessUser } = channelAccess

    const isActiveEntry = (entry: IChannelAccessEntry) => !!emulated_user && user.channel_id === entry.channel.id

    return (
        <>
            <li className={dividerClassName} role="separator" />
            {accessUser && (
                <li>
                    <a href="/api/v4/channels/access/logout" className={rowClassName}>
                        <Avatar user={accessUser} width={20} height={20} className="shrink-0 rounded-sm" />
                        <span className="flex-1 truncate">{accessUser.display_name || accessUser.username}</span>
                        {!emulated_user ? <CheckMarkIcon /> : <AccessUserIcon />}
                    </a>
                </li>
            )}
            {access.length > 10 ? (
                <>
                    {showAll ? (
                        <div className="max-h-[245px] overflow-x-hidden overflow-y-auto">
                            <ul>
                                {access.map((entry) => (
                                    <AccessEntry key={entry.id} access={entry} isActive={isActiveEntry(entry)} />
                                ))}
                            </ul>
                        </div>
                    ) : (
                        <AccessEntry access={access[0]} isActive={isActiveEntry(access[0])} />
                    )}
                    {showAll && <li className={dividerClassName} role="separator" />}
                    <li>
                        <button type="button" onClick={() => setShowAll((prev) => !prev)} className={rowClassName}>
                            <span className="flex size-5 shrink-0 items-center justify-center rounded-sm bg-giphyDarkCharcoal text-xs font-bold text-white">
                                {access.length}
                            </span>
                            <span className="flex-1 truncate">{showAll ? 'Hide Accounts' : 'See All Accounts'}</span>
                            <ChevronIcon className={showAll ? 'ml-auto shrink-0 rotate-180' : 'ml-auto shrink-0'} />
                        </button>
                    </li>
                </>
            ) : (
                access.map((entry) => <AccessEntry key={entry.id} access={entry} isActive={isActiveEntry(entry)} />)
            )}
        </>
    )
}

export default ChannelAccessList
